import { isMoveType, type PlaceLookup, stayShortName } from './items';
import type { Item, Place } from './schema';
import { diffDays, localDate } from './time';
import type { TripSummary } from './trip-model';

/** What the Now card shows, in order of priority. */
export type NowState =
  | {
      kind: 'in-flight';
      item: Item;
      from: Place | undefined;
      to: Place | undefined;
      departedAt: number;
      arrivesAt: number;
      progress: number;
      tripId: string | null;
    }
  | {
      kind: 'travel-day';
      item: Item;
      from: Place | undefined;
      to: Place | undefined;
      departsAt: number;
      tripId: string | null;
    }
  | {
      kind: 'on-trip';
      trip: TripSummary;
      place: Place | undefined;
      tonight: Item | null;
      nightNumber: number | null;
      nights: number | null;
      next: NextPlan | null;
    }
  | { kind: 'upcoming'; trip: TripSummary }
  | { kind: 'none' };

export interface NextPlan {
  item: Item;
  label: string;
  at: number;
  tz: string;
}

const TRAVEL_DAY_MS = 18 * 3_600_000;

/** Departure and arrival instants of a move, preferring live times when given. */
export interface MoveTimes {
  depart: number;
  arrive: number;
}

function nextPlanAfter(items: Item[], now: number, places: PlaceLookup): NextPlan | null {
  const events: NextPlan[] = [];
  for (const i of items) {
    if (i.deleted || i.status !== 'active' || i.startAt == null || !i.startTz) continue;
    if (i.type === 'lodging') {
      if (i.startAt > now)
        events.push({
          item: i,
          label: `Check in at ${stayShortName(i)}`,
          at: i.startAt,
          tz: i.startTz,
        });
      if (i.endAt != null && i.endAt > now) {
        events.push({
          item: i,
          label: `Check out of ${stayShortName(i)}`,
          at: i.endAt,
          tz: i.endTz ?? i.startTz,
        });
      }
      continue;
    }
    if (i.type === 'car') {
      const company = i.details.type === 'car' ? i.details.company : '';
      if (i.startAt > now)
        events.push({
          item: i,
          label: `Pick up ${company} car`.replace('  ', ' '),
          at: i.startAt,
          tz: i.startTz,
        });
      if (i.endAt != null && i.endAt > now) {
        events.push({
          item: i,
          label: `Return ${company} car`.replace('  ', ' '),
          at: i.endAt,
          tz: i.endTz ?? i.startTz,
        });
      }
      continue;
    }
    if (i.startAt > now) {
      const label = isMoveType(i.type)
        ? `${i.type === 'flight' ? 'Fly' : 'Travel'} to ${places(i.endPlaceId)?.city ?? places(i.endPlaceId)?.name ?? 'next stop'}`
        : i.title;
      events.push({ item: i, label, at: i.startAt, tz: i.startTz });
    }
  }
  events.sort((a, b) => a.at - b.at);
  return events[0] ?? null;
}

export function computeNow(opts: {
  now: number;
  items: Item[];
  trips: TripSummary[];
  itemsByTrip: Map<string, Item[]>;
  places: PlaceLookup;
  /** Live departure and arrival for flights, by item id. */
  moveTimes?: (item: Item) => MoveTimes | null;
  today: string;
}): NowState {
  const { now, items, places } = opts;
  const times = (i: Item): MoveTimes | null =>
    opts.moveTimes?.(i) ??
    (i.startAt != null && i.endAt != null ? { depart: i.startAt, arrive: i.endAt } : null);

  const flights = items
    .filter((i) => !i.deleted && i.status === 'active' && i.type === 'flight')
    .map((i) => ({ item: i, t: times(i) }))
    .filter((f): f is { item: Item; t: MoveTimes } => !!f.t)
    .sort((a, b) => a.t.depart - b.t.depart);

  const airborne = flights.find((f) => f.t.depart <= now && now < f.t.arrive);
  if (airborne) {
    const span = airborne.t.arrive - airborne.t.depart;
    return {
      kind: 'in-flight',
      item: airborne.item,
      from: places(airborne.item.startPlaceId),
      to: places(airborne.item.endPlaceId),
      departedAt: airborne.t.depart,
      arrivesAt: airborne.t.arrive,
      progress: span > 0 ? Math.min(1, Math.max(0, (now - airborne.t.depart) / span)) : 0,
      tripId: airborne.item.tripId,
    };
  }

  const nextFlight = flights.find((f) => f.t.depart > now && f.t.depart - now <= TRAVEL_DAY_MS);
  if (nextFlight) {
    return {
      kind: 'travel-day',
      item: nextFlight.item,
      from: places(nextFlight.item.startPlaceId),
      to: places(nextFlight.item.endPlaceId),
      departsAt: nextFlight.t.depart,
      tripId: nextFlight.item.tripId,
    };
  }

  const current = opts.trips.find((t) => t.phase === 'current');
  if (current) {
    const tripItems = opts.itemsByTrip.get(current.id) ?? [];
    const tonight =
      tripItems.find((i) => {
        if (
          i.deleted ||
          i.status !== 'active' ||
          i.type !== 'lodging' ||
          i.startAt == null ||
          i.endAt == null
        )
          return false;
        const tz = i.startTz ?? 'UTC';
        const d = localDate(now, tz);
        return localDate(i.startAt, tz) <= d && d < localDate(i.endAt, i.endTz ?? tz);
      }) ?? null;
    let nightNumber: number | null = null;
    let nights: number | null = null;
    if (tonight?.startAt != null && tonight.endAt != null && tonight.startTz) {
      const tz = tonight.startTz;
      const start = localDate(tonight.startAt, tz);
      nightNumber = diffDays(start, localDate(now, tz)) + 1;
      nights = diffDays(start, localDate(tonight.endAt, tonight.endTz ?? tz));
    }
    // Where I am: tonight's stay, else the last place I arrived.
    const arrived = tripItems
      .filter((i) => !i.deleted && i.status === 'active' && i.startAt != null && i.startAt <= now)
      .sort((a, b) => (b.startAt ?? 0) - (a.startAt ?? 0))[0];
    const place = tonight
      ? places(tonight.startPlaceId)
      : arrived
        ? places(isMoveType(arrived.type) ? arrived.endPlaceId : arrived.startPlaceId)
        : undefined;
    return {
      kind: 'on-trip',
      trip: current,
      place,
      tonight,
      nightNumber,
      nights,
      next: nextPlanAfter(tripItems, now, places),
    };
  }

  const upcoming = opts.trips
    .filter((t) => t.phase === 'upcoming' && t.daysUntil != null && t.daysUntil <= 60)
    .sort((a, b) => (a.daysUntil ?? 0) - (b.daysUntil ?? 0))[0];
  if (upcoming) return { kind: 'upcoming', trip: upcoming };
  return { kind: 'none' };
}
